import styled from "@emotion/styled"
import { useRef, createContext, useLayoutEffect } from "react"
import { useConsole } from "contexts/Console"
import { useMotionValueEvent } from "framer-motion"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { contrastMode } from "css/a11y"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { MoonCalendarProvider, useMoonCalendar, rafDelay } from "./context"
import ImageCLD from "components/media/ImageCLD"
import { cssVars } from "components/nav-v2/Wrapper"
import { MoonphaseCalendarProvider } from "contexts/MoonphaseCalendar"
import MoonCalendarHeader from "./Header"
import MoonCalendarDisc from "./Disc"
import MoonCalendarDate from "./Date"
import MoonCalendarNav from "./Nav"
import MoonCalendarDrag from "./Drag"
import { visuallyHidden } from "css/text"
import useSSR from "hooks/useSSR"

const Main = styled.section`
  position: relative;
  overflow: hidden;
  user-select: none;
  --mc-vp-height: calc(100lvh - var(${cssVars.main.safe}));
  --mc-min-height: 700px;
  --mc-height: max(var(--mc-vp-height), var(--mc-min-height));
  height: var(--mc-height);
  transform-style: preserve-3d;

  ${getMediaQuery("m")} {
    --mc-min-height: 600px;
  }

  ${fullGrid}
  justify-items: center;
  align-items: center;
  grid-template-rows: min-content 1fr;

  --mc-disc-w: 500px;
  ${getMediaQuery("xs")} {
    --mc-disc-w: 600px;
  }

  ${getMediaQuery("s")} {
    --mc-disc-w: 480px;
  }
`

const H1 = styled.h1`
  ${visuallyHidden}
`

const CoverBackground = styled.div`
  position: relative;
  grid-row: 1/-1;
  grid-column: 1/-1;
  width: 100%;
  height: 100%;

  img {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

function MoonCalendarContent(props) {
  const console = useConsole()
  const { ctx, background } = props
  const { loaded, ended } = useMoonCalendar(ctx)
  const rfmain = useRef(null)

  function onLoaded(v) {
    if (!v) return
    rfmain.current?.classList.add("mc-loaded")
  }
  useMotionValueEvent(loaded, "change", onLoaded)

  function onEnded(v) {
    if (!v) return
    rfmain.current?.classList.add("mc-ended")
  }
  useMotionValueEvent(ended, "change", onEnded)

  return (
    <>
      <Main
        ref={rfmain}
      >
        <CoverBackground>
          <ImageCLD sources={background} sizes='auto' alt='' />
        </CoverBackground>
        <MoonCalendarDate {...props} />
        <MoonCalendarDisc {...props} />
        <MoonCalendarDrag {...props} />
      </Main>
      <MoonCalendarNav {...props} />
    </>
  )
}

export default function MoonCalendar(props) {
  const console = useConsole()
  const ctx = createContext()
  const ssr = useSSR()
  let { children } = props

  return (
    <>
      {ssr ? <H1 aria-hidden={true}>{props.heading.title}</H1> : null}
      <MoonCalendarProvider
        Ctx={ctx}
        key={Date.now()}
      >
        <MoonphaseCalendarProvider>
          <MoonCalendarContent ctx={ctx} {...props}>
            {children}
          </MoonCalendarContent>
        </MoonphaseCalendarProvider>
      </MoonCalendarProvider>
    </>
  )
}
